import {
  Barcode,
  CalendarDays,
  Copy,
  CreditCard,
  DollarSign,
  Hash,
  MapPin,
  Package,
  Plus,
  ScanLine,
  Tag,
  Trash2,
  Truck,
  Type,
} from 'lucide-react'
import type { ReactNode } from 'react'

import {
  LABEL_FIELD_SOURCES,
  type LabelFieldSourceType,
} from '@aeris/contracts'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@aeris/ui'

import {
  FIELD_LABELS,
  describeElement,
  formatDecimalInput,
  type DraftElement,
} from '../../lib/label-template-draft.ts'

/**
 * A lista de campos da etiqueta (F129).
 *
 * Substitui a tabela de dezessete colunas, onde rolar até a fonte escondia a
 * coluna que dizia qual campo era aquele — seis linhas iguais e ninguém sabia
 * qual era o preço. Aqui cada linha diz o que é, com ícone, e o resto das
 * propriedades mora no painel do lado.
 */

const ICON = 'h-4 w-4'

const iconFor = (element: DraftElement): ReactNode => {
  if (element.kind === 'barcode') {
    return <Barcode aria-hidden="true" className={ICON} />
  }
  switch (element.source) {
    case 'salePrice':
      return <DollarSign aria-hidden="true" className={ICON} />
    case 'installment':
      return <CreditCard aria-hidden="true" className={ICON} />
    case 'sku':
      return <Hash aria-hidden="true" className={ICON} />
    case 'gtin':
      return <ScanLine aria-hidden="true" className={ICON} />
    case 'brand':
      return <Tag aria-hidden="true" className={ICON} />
    case 'unit':
      return <Package aria-hidden="true" className={ICON} />
    case 'supplierReference':
      return <Truck aria-hidden="true" className={ICON} />
    case 'location':
      return <MapPin aria-hidden="true" className={ICON} />
    case 'lastPurchaseAt':
    case 'printedAt':
      return <CalendarDays aria-hidden="true" className={ICON} />
    case 'name':
    case 'literal':
      return <Type aria-hidden="true" className={ICON} />
  }
}

const detailOf = (element: DraftElement): string =>
  element.kind === 'barcode'
    ? `${formatDecimalInput(element.widthMm)} × ${formatDecimalInput(element.heightMm)} mm`
    : `${element.fontFamily} · ${formatDecimalInput(element.fontSizePt)} pt`

export const FieldList = ({
  elements,
  selected,
  onSelect,
  onAddText,
  onAddBarcode,
  onDuplicate,
  onRemove,
}: {
  elements: readonly DraftElement[]
  selected: number | null
  onSelect: (index: number) => void
  onAddText: (source: LabelFieldSourceType) => void
  onAddBarcode: () => void
  onDuplicate: (index: number) => void
  onRemove: (index: number) => void
}): ReactNode => (
  <div className="flex min-h-0 flex-1 flex-col">
    <div className="flex items-center justify-between px-4 pt-3 pb-2">
      <h2 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Campos
      </h2>
      <span className="text-(length:--text-xs) text-(--text-muted) tabular-nums">
        {elements.length}
      </span>
    </div>

    <ul
      aria-label="Campos da etiqueta"
      className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-auto px-2 pb-2"
    >
      {elements.length === 0 ? (
        <li className="px-3 py-8 text-center text-(length:--text-sm) text-(--text-muted)">
          A etiqueta está vazia. Adicione um campo aqui embaixo.
        </li>
      ) : (
        elements.map((element, index) => {
          const active = index === selected
          const name = describeElement(element)
          return (
            <li
              key={index}
              className={cn(
                'group flex items-center gap-1 rounded-(--radius-md)',
                active
                  ? 'bg-(--brand-primary)/10 ring-1 ring-(--brand-primary)'
                  : 'hover:bg-(--surface-sunken)'
              )}
            >
              <button
                type="button"
                aria-pressed={active}
                onClick={() => {
                  onSelect(index)
                }}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-(--radius-md) px-2 py-1.5 text-left focus-visible:ring-2 focus-visible:ring-(--brand-primary) focus-visible:outline-none"
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-(--radius-sm)',
                    active
                      ? 'bg-(--brand-primary) text-(--text-on-brand)'
                      : 'bg-(--surface-sunken) text-(--text-secondary)'
                  )}
                >
                  {iconFor(element)}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-(length:--text-sm) text-(--text-primary)">
                    {name}
                  </span>
                  <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {detailOf(element)}
                  </span>
                </span>
              </button>
              <div
                className={cn(
                  'flex shrink-0 pr-1',
                  active
                    ? ''
                    : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'
                )}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Duplicar ${name}`}
                  leadingIcon={
                    <Copy aria-hidden="true" className="h-3.5 w-3.5" />
                  }
                  onClick={() => {
                    onDuplicate(index)
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Excluir ${name}`}
                  leadingIcon={
                    <Trash2
                      aria-hidden="true"
                      className="h-3.5 w-3.5 text-(--danger)"
                    />
                  }
                  onClick={() => {
                    onRemove(index)
                  }}
                />
              </div>
            </li>
          )
        })
      )}
    </ul>

    <div className="flex gap-2 border-t border-(--border-subtle) px-3 py-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className="flex-1"
            aria-label="Adicionar campo"
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
          >
            Campo
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {LABEL_FIELD_SOURCES.map((source) => (
            <DropdownMenuItem
              key={source}
              onSelect={() => {
                onAddText(source)
              }}
            >
              {FIELD_LABELS[source]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        variant="secondary"
        size="sm"
        className="flex-1"
        aria-label="Adicionar barras"
        leadingIcon={<Barcode aria-hidden="true" className="h-4 w-4" />}
        onClick={onAddBarcode}
      >
        Barras
      </Button>
    </div>
  </div>
)
